import {
  createInventoryItem,
  discontinueInventoryItem,
  getInventoryItemDetail,
  INVENTORY_CACHE_PREFIXES,
  patchInventoryItem,
  reactivateInventoryItem,
  unarchiveInventoryItem,
  type CompatibleMakeRef,
  type CompatibleModelRef,
} from '@/lib/api/inventory.js';
import { listInventoryCategories } from '@/lib/api/inventory-categories.js';
import { StockAdjustModal } from '@/components/inventory/StockAdjustModal.js';
import {
  ArchiveInventoryModal,
  DeleteInventoryModal,
} from '@/components/inventory/InventoryArchiveModals.js';
import { AboutSection } from '@/components/inventory/item/AboutSection.js';
import { BundleSection } from '@/components/inventory/item/BundleSection.js';
import { CompatibleSection } from '@/components/inventory/item/CompatibleSection.js';
import { DescriptionSection } from '@/components/inventory/item/DescriptionSection.js';
import { HistorySection } from '@/components/inventory/item/HistorySection.js';
import {
  flattenCategories,
  INVENTORY_ITEM_SECTIONS,
  str,
} from '@/components/inventory/item/item-form-utils.js';
import { PricingSection } from '@/components/inventory/item/PricingSection.js';
import { StockSection } from '@/components/inventory/item/StockSection.js';
import { SuppliersSection } from '@/components/inventory/item/SuppliersSection.js';
import { VariantsSection } from '@/components/inventory/item/VariantsSection.js';
import { formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { isManagerRole } from '@/lib/permissions.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useMatch, useNavigate, useParams } from 'react-router-dom';
import { Archive, ArrowLeft, Ban, MoreVertical, Printer, Trash2 } from 'lucide-react';
import { PrintInventoryLabelsModal } from '@/components/inventory/PrintInventoryLabelsModal.js';
import { ReportDamageModal } from '@/components/inventory/ReportDamageModal.js';
import { MergeInventoryModal } from '@/components/inventory/MergeInventoryModal.js';
import type { InventoryDuplicateCard } from '@/lib/api/inventory-merge.js';
import { printInventoryLabelsWithFallback } from '@/lib/print.js';

export function InventoryItemPage(): ReactElement {
  const matchNew = useMatch({ path: '/inventory/new', end: true });
  const isCreate = Boolean(matchNew);
  const { id: paramId = '' } = useParams<{ id: string }>();
  const itemId = isCreate ? '' : paramId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? 'read_only');
  const canArchive = isManagerRole(role);
  const canHardDelete = role === 'tenant_admin' || role === 'platform_admin';
  const [activeSection, setActiveSection] = useState<string>('about');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [damageOpen, setDamageOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['inventory-item', itemId],
    queryFn: () => getInventoryItemDetail(itemId),
    enabled: Boolean(itemId),
  });
  const categoriesQuery = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: listInventoryCategories,
  });

  const item = detailQuery.data?.item ?? {};
  const stock = detailQuery.data?.stock;
  const suppliers = detailQuery.data?.suppliers ?? [];
  const history = detailQuery.data?.history ?? detailQuery.data?.adjustments ?? [];
  const bundleComponents = detailQuery.data?.bundleComponents ?? [];
  const variants = detailQuery.data?.variants ?? [];

  const [name, setName] = useState('');
  const [category, setCategory] = useState('repair_part');
  const [inventoryCategoryId, setInventoryCategoryId] = useState('');
  const [condition, setCondition] = useState('new');
  const [description, setDescription] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [retailPrice, setRetailPrice] = useState('0');
  const [costPrice, setCostPrice] = useState('0');
  const [upc, setUpc] = useState('');
  const [mpn, setMpn] = useState('');
  const [color, setColor] = useState('');
  const [compatibleMakes, setCompatibleMakes] = useState<CompatibleMakeRef[]>([]);
  const [compatibleModels, setCompatibleModels] = useState<CompatibleModelRef[]>([]);

  useEffect(() => {
    if (!detailQuery.data) return;
    const it = detailQuery.data.item;
    setName(str(it.name));
    setCategory(str(it.category, 'repair_part'));
    setInventoryCategoryId(str(it.inventoryCategoryId));
    setCondition(str(it.condition, 'new'));
    setDescription(str(it.description));
    setInternalNotes(str(it.internalNotes));
    setRetailPrice(str(it.retailPrice, '0'));
    setCostPrice(str(it.costPrice, '0'));
    setUpc(str(it.upc));
    setMpn(str(it.manufacturerPartNumber));
    setColor(str(it.color));
    setCompatibleMakes((it.compatibleMakes as CompatibleMakeRef[]) ?? []);
    setCompatibleModels((it.compatibleModels as CompatibleModelRef[]) ?? []);
  }, [detailQuery.data]);

  const categoryOptions = useMemo(
    () => flattenCategories(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isCreate) {
        return createInventoryItem({
          name,
          category,
          inventoryCategoryId: inventoryCategoryId || null,
          condition,
          upc: upc.trim() || null,
          manufacturerPartNumber: mpn.trim() || null,
          color: color.trim() || null,
          compatibleMakes,
          compatibleModels,
          retailPrice: Number(retailPrice) || 0,
          taxable: true,
          trackStock: true,
        });
      }
      if (!itemId) throw new Error('Item id is required');
      return patchInventoryItem(itemId, {
        name,
        description: description || null,
        inventoryCategoryId: inventoryCategoryId || null,
        condition,
        upc: upc || null,
        manufacturerPartNumber: mpn || null,
        color: color || null,
        retailPrice: Number(retailPrice) || 0,
        costPrice: Number(costPrice) || 0,
        internalNotes: internalNotes || null,
        compatibleMakes,
        compatibleModels,
      });
    },
    onSuccess: (data) => {
      toastSuccess(isCreate ? 'Item created' : 'Item saved');
      if (isCreate && data?.id) {
        navigate(`/inventory/${data.id}`);
      } else {
        void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function invalidateItem() {
    for (const key of INVENTORY_CACHE_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }

  const unarchiveMut = useMutation({
    mutationFn: () => {
      if (!itemId) throw new Error('Item id is required');
      return unarchiveInventoryItem(itemId);
    },
    onSuccess: () => {
      toastSuccess('Item restored');
      invalidateItem();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const discontinueMut = useMutation({
    mutationFn: () => {
      if (!itemId) throw new Error('Item id is required');
      return discontinueInventoryItem(itemId);
    },
    onSuccess: () => {
      toastSuccess('Item discontinued');
      invalidateItem();
      setMenuOpen(false);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const reactivateMut = useMutation({
    mutationFn: () => {
      if (!itemId) throw new Error('Item id is required');
      return reactivateInventoryItem(itemId);
    },
    onSuccess: () => {
      toastSuccess('Item reactivated');
      invalidateItem();
      setMenuOpen(false);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function scrollTo(id: string) {
    setActiveSection(id);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const qtyOnHand = Number(stock?.qtyOnHand ?? 0);
  const qtyReserved = Number((item as { qtyReserved?: number }).qtyReserved ?? stock?.qtyReserved ?? 0);
  const qtyAvailable = Number(
    (item as { qtyAvailable?: number }).qtyAvailable ?? Math.max(0, qtyOnHand - qtyReserved),
  );
  const archivedAt = item.archivedAt ? String(item.archivedAt) : null;
  const discontinuedAt = item.discontinuedAt ? String(item.discontinuedAt) : null;
  const archiveReason = item.archiveReason ? String(item.archiveReason) : null;

  if (!isCreate && detailQuery.isLoading) {
    return <div className="p-6 text-slate-500">Loading item…</div>;
  }

  return (
    <div className="space-y-4">
      {!isCreate && archivedAt ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm">
          <div>
            <p className="font-semibold text-slate-900">
              Archived {formatDate(archivedAt)}
              {archiveReason ? ` — ${archiveReason}` : ''}
            </p>
            <p className="text-slate-600">Hidden from POS and pickers. History is preserved.</p>
          </div>
          {canArchive ? (
            <button
              type="button"
              className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white"
              onClick={() => unarchiveMut.mutate()}
            >
              Unarchive
            </button>
          ) : null}
        </div>
      ) : null}
      {!isCreate && discontinuedAt && !archivedAt ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <p className="font-semibold text-amber-950">
            Discontinued — visible in inventory but not on new tickets or POs
          </p>
          {canArchive ? (
            <button
              type="button"
              className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold"
              onClick={() => reactivateMut.mutate()}
            >
              Reactivate
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/inventory" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-950">
              {isCreate ? 'New inventory item' : name || 'Inventory item'}
            </h1>
            {!isCreate ? (
              <p className="text-sm text-slate-500">
                {str(item.barcodeInternal)}
                {item.categoryPath ? ` · ${String(item.categoryPath)}` : ''}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isCreate ? (
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setDamageOpen(true)}
            >
              Report damage
            </button>
          ) : null}
          {!isCreate && canArchive ? (
            <div className="relative">
              <button
                type="button"
                className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="More actions"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              {menuOpen ? (
                <div className="absolute right-0 z-20 mt-1 w-48 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  {!archivedAt ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => {
                        setMenuOpen(false);
                        setMergeOpen(true);
                      }}
                    >
                      Merge with another item
                    </button>
                  ) : null}
                  {!archivedAt ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => {
                        setMenuOpen(false);
                        setArchiveOpen(true);
                      }}
                    >
                      <Archive className="h-4 w-4" /> Archive
                    </button>
                  ) : null}
                  {!archivedAt && !discontinuedAt ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => discontinueMut.mutate()}
                    >
                      <Ban className="h-4 w-4" /> Discontinue
                    </button>
                  ) : null}
                  {canHardDelete ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setMenuOpen(false);
                        setDeleteOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setPrintOpen(true)}
            disabled={isCreate || !itemId}
            className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            Print label
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !name.trim()}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : isCreate ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {!isCreate ? (
          <nav className="sticky top-4 hidden h-fit w-44 shrink-0 space-y-1 lg:block">
            {INVENTORY_ITEM_SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollTo(s.id)}
                className={[
                  'block w-full rounded-lg px-3 py-2 text-left text-sm font-medium',
                  activeSection === s.id
                    ? 'bg-slate-100 text-slate-950'
                    : 'text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {s.label}
              </button>
            ))}
          </nav>
        ) : null}

        <div className="min-w-0 flex-1 space-y-6">
          <AboutSection
            isCreate={isCreate}
            name={name}
            onNameChange={setName}
            category={category}
            onCategoryChange={setCategory}
            inventoryCategoryId={inventoryCategoryId}
            onInventoryCategoryIdChange={setInventoryCategoryId}
            condition={condition}
            onConditionChange={setCondition}
            upc={upc}
            onUpcChange={setUpc}
            mpn={mpn}
            onMpnChange={setMpn}
            color={color}
            onColorChange={setColor}
            categoryOptions={categoryOptions}
          />

          <CompatibleSection
            partName={name}
            compatibleMakes={compatibleMakes}
            onCompatibleMakesChange={setCompatibleMakes}
            compatibleModels={compatibleModels}
            onCompatibleModelsChange={setCompatibleModels}
          />

          {!isCreate ? (
            <>
              <DescriptionSection
                description={description}
                onDescriptionChange={setDescription}
                internalNotes={internalNotes}
                onInternalNotesChange={setInternalNotes}
              />
              <StockSection
                qtyOnHand={qtyOnHand}
                qtyReserved={qtyReserved}
                qtyAvailable={qtyAvailable}
                binLocation={stock?.binLocation}
                qtyOnOrder={stock?.qtyOnOrder}
                onAdjust={() => setAdjustOpen(true)}
              />
              <SuppliersSection itemId={itemId} suppliers={suppliers} />
              <PricingSection
                retailPrice={retailPrice}
                onRetailPriceChange={setRetailPrice}
                costPrice={costPrice}
                onCostPriceChange={setCostPrice}
              />
              <BundleSection
                itemId={itemId}
                isBundle={Boolean(item.isBundle)}
                bundleComponents={bundleComponents}
              />
              <VariantsSection
                itemId={itemId}
                hasVariants={Boolean(item.hasVariants)}
                variants={variants}
              />
              <HistorySection history={history} />
            </>
          ) : null}
        </div>
      </div>

      {!isCreate && itemId ? (
        <>
          <StockAdjustModal
            open={adjustOpen}
            onClose={() => setAdjustOpen(false)}
            itemId={itemId}
            itemName={name}
            currentQty={qtyOnHand}
            suppliers={suppliers.map((s) => {
              const row: { id: string; supplierName?: string; supplierId?: string } = {
                id: String(s.id),
              };
              if (typeof s.supplierName === 'string') row.supplierName = s.supplierName;
              if (typeof s.supplierId === 'string') row.supplierId = s.supplierId;
              return row;
            })}
            onSuccess={() => void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] })}
            onPrintedLabelsOffer={async (qtyAdded) => {
              if (qtyAdded <= 0) return;
              if (!window.confirm(`Print ${qtyAdded} label(s) for ${name}?`)) return;
              await printInventoryLabelsWithFallback([{ itemId, quantity: qtyAdded }]);
            }}
          />
          <PrintInventoryLabelsModal
            open={printOpen}
            onClose={() => setPrintOpen(false)}
            title="Print inventory label"
            items={[{ itemId, name, quantity: 1 }]}
          />
          <ReportDamageModal
            open={damageOpen}
            itemId={itemId}
            itemName={name}
            serials={detailQuery.data?.serials}
            onClose={() => {
              setDamageOpen(false);
              invalidateItem();
            }}
          />
          <MergeInventoryModal
            open={mergeOpen}
            seedId={itemId}
            items={[
              {
                id: itemId,
                name: name || 'Item',
                sku: str(item.sku) || null,
                upc: upc || null,
                manufacturerPartNumber: mpn || null,
                condition: condition || null,
                costPrice: costPrice || '0',
                retailPrice: retailPrice || '0',
                minimumPrice: str(item.minimumPrice) || null,
                category,
                description: description || null,
                color: color || null,
                taxable: true,
                qtyOnHand: String(qtyOnHand),
                createdAt: str(item.createdAt) || new Date(0).toISOString(),
                suppliers: suppliers.flatMap((row) => {
                  const supplierId = typeof row.supplierId === 'string' ? row.supplierId : '';
                  if (!supplierId) return [];
                  return [
                    {
                      supplierId,
                      supplierName: typeof row.supplierName === 'string' ? row.supplierName : 'Supplier',
                      supplierSku: typeof row.supplierSku === 'string' ? row.supplierSku : null,
                      costPrice: String(row.costPrice ?? '0'),
                      isPreferred: Boolean(row.preferred),
                      lastOrderedAt: null,
                    },
                  ];
                }),
                suggestedSupplier: null,
              },
            ]}
            onClose={() => setMergeOpen(false)}
            onMerged={() => {
              setMergeOpen(false);
              invalidateItem();
            }}
          />
        </>
      ) : null}

      {!isCreate && itemId ? (
        <>
          <ArchiveInventoryModal
            open={archiveOpen}
            item={{ id: itemId, name, qtyOnHand: String(qtyOnHand) }}
            onClose={() => setArchiveOpen(false)}
            onDone={invalidateItem}
          />
          <DeleteInventoryModal
            open={deleteOpen}
            item={{ id: itemId, name }}
            onClose={() => setDeleteOpen(false)}
            onDone={() => {
              invalidateItem();
              navigate('/inventory');
            }}
            onArchiveInstead={() => {
              setDeleteOpen(false);
              setArchiveOpen(true);
            }}
          />
        </>
      ) : null}
    </div>
  );
}
